import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";
import DateRangeFilter from "components/common/DateRangeFilter";
import FullPageLoader from "components/common/FullPageLoader";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { getBackgroundStyle } from "components/common/TableColor";
import { CheckinCheckoutAdjustmentModal } from "components/appointment/checkinCheckoutAdjustment";
import { payrollSelector, fetchPayroll, downloadPayroll } from "slices/payrollSlice";
import { setActionAssign, actionButtonsSelector } from "slices/actionButtonSlice";
import { usePermissions } from "layouts/Admin";
import { useForm } from "react-hook-form";

// Design shows durations as H:MM (e.g. 10:00, 2:30)
const formatMinutes = (minutes) => {
    const m = Math.max(0, Number(minutes) || 0);
    return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
};

const formatDate = (date) => date ? moment(date, 'YYYY-MM-DD').format('MMM D, YYYY') : '—';

const formatStartTime = (time) => time ? moment(time, 'HH:mm:ss').format('h:mm A') : '—';

// "May 4, 2026 10:00 AM – 11:30 AM"; start only when there is no end time
const formatDateTimeRange = (record) => {
    if (!record.date) return '—';
    const date = formatDate(record.date);
    if (!record.start_time) return date;
    const start = formatStartTime(record.start_time);
    const end = record.end_time ? ` – ${formatStartTime(record.end_time)}` : '';
    return `${date} ${start}${end}`;
};

// "May 4, 2026 10:00 AM" from an assign date + time pair
const formatAssignDateTime = (date, time) => {
    if (!date) return '—';
    return `${formatDate(date)}${time ? ' ' + formatStartTime(time) : ''}`;
};

// Matches the dashboard's travel time row highlight (views/Dashboard.jsx)
const TRAVEL_TIME_ROW_STYLE = { background: '#ff69b433' };

// eslint-disable-next-line eqeqeq
const typeLabel = (record) => record.is_travel ? 'Travel Time' : (record.type == 1 ? 'After Hours' : 'Interpreting Request');

// Sort keys for the client-side sortable detail table. date is Y-m-d and the
// times are zero-padded 24h, so plain string comparison sorts chronologically.
const recordSortValue = (record, by) => {
    switch (by) {
        case 'appid': return record.appid || '';
        case 'type': return typeLabel(record);
        case 'datetime': return `${record.date || ''} ${record.start_time || ''}`;
        case 'school': return record.is_travel ? '' : (record.school || '');
        case 'checkin': return record.appointment_assign?.checkin_date
            ? `${record.appointment_assign.checkin_date} ${record.appointment_assign.checkin_time || ''}` : '';
        case 'checkout': return record.appointment_assign?.checkout_date
            ? `${record.appointment_assign.checkout_date} ${record.appointment_assign.checkout_time || ''}` : '';
        case 'duration': return record.duration_minutes || 0;
        case 'status': return record.is_travel ? 'travel time' : (record.status || '');
        default: return '';
    }
};

const downloadBlobFile = (blob, filename = "payroll.xlsx") => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const AvatarIcon = () => (
    <span className="payroll_avatar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.34 0-10 1.67-10 5v3h20v-3c0-3.33-6.66-5-10-5z" />
        </svg>
    </span>
);

const SummaryCard = ({ icon, label, value }) => (
    <div className="card_table p-4">
        <div className="d-flex align-items-center" style={{ gap: 14 }}>
            <span className="payroll_icon_circle"><i className={`fa-solid ${icon}`}></i></span>
            <div>
                <div style={{ color: '#809FB8', fontSize: 14 }}>{label}</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
            </div>
        </div>
    </div>
);

const SORTABLE_COLUMNS = [
    { key: 'appid', title: 'Appt ID' },
    { key: 'type', title: 'Type' },
    { key: 'datetime', title: 'Date & Time' },
    { key: 'school', title: 'School' },
    { key: 'checkin', title: 'Checkin Date & Time' },
    { key: 'checkout', title: 'Checkout Date & Time' },
    { key: 'duration', title: 'Total Duration' },
    { key: 'status', title: 'Status' },
];

const DEFAULT_SORT = { by: 'datetime', dir: 'desc' }; // latest first

const InterpreterRow = ({
    interpreter, isOpen, onToggle, onCheckinCheckout, onExport, exporting, canCheckin
}) => {
    const hasRecords = interpreter.records?.length > 0;
    const meetingCount = interpreter.meeting_count ?? interpreter.records?.filter(r => !r.is_travel).length ?? 0;
    const travelCount = (interpreter.records?.length || 0) - meetingCount;
    const [sort, setSort] = useState(DEFAULT_SORT);

    // Fresh sort whenever the accordion is reopened
    useEffect(() => {
        if (isOpen) setSort(DEFAULT_SORT);
    }, [isOpen]);

    const sortedRecords = useMemo(() => {
        const records = [...(interpreter.records || [])];
        records.sort((a, b) => {
            const va = recordSortValue(a, sort.by);
            const vb = recordSortValue(b, sort.by);
            const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
            return sort.dir === 'desc' ? -cmp : cmp;
        });
        return records;
    }, [interpreter.records, sort]);

    const headMeta = hasRecords
        ? `${meetingCount} appointment${meetingCount === 1 ? '' : 's'}${travelCount > 0 ? ` • ${travelCount} travel entr${travelCount === 1 ? 'y' : 'ies'}` : ''}`
        : 'No appointments in selected range';

    return (
        <div className={`payroll_row${isOpen ? ' open' : ''}`}>
            <div className="payroll_row_head" onClick={onToggle}>
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <AvatarIcon />
                    <h2>{interpreter.name}</h2>
                </div>
                <div className="payroll_row_meta">
                    <span className="payroll_muted">{headMeta}</span>
                    {hasRecords &&
                        <button
                            type="button"
                            className="payroll_action_btn"
                            disabled={exporting}
                            onClick={(e) => { e.stopPropagation(); onExport(interpreter); }}
                        >
                            {exporting ? 'Exporting…' : 'Export'}
                        </button>
                    }
                    <span className="payroll_total">{formatMinutes(interpreter.total_minutes)}</span>
                    <span className="payroll_chevron">&#9662;</span>
                </div>
            </div>

            {isOpen && (
                <div className="payroll_detail">
                    {hasRecords ? (
                        <>
                            <div className="payroll_table_wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th className="payroll_num_col">#</th>
                                            {SORTABLE_COLUMNS.map((col) => (
                                                <th key={col.key}>
                                                    <TableHeaderItem
                                                        title={col.title}
                                                        sortBy={col.key}
                                                        activeSortBy={sort.by}
                                                        sortDirection={sort.dir}
                                                        callBack={(dir, by) => setSort({ by, dir })}
                                                    />
                                                </th>
                                            ))}
                                            {canCheckin && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => { let apptNo = 0; return sortedRecords.map((record) => {
                                            // Same status color coding as the dashboard rows
                                            const cellStyle = record.is_travel ? TRAVEL_TIME_ROW_STYLE : getBackgroundStyle(record.status);
                                            if (!record.is_travel) apptNo += 1;
                                            return (
                                                <tr key={record.id}>
                                                    <td style={cellStyle} className={record.is_travel ? 'p-0' : undefined}>
                                                        {record.is_travel
                                                            ? <div className="payroll_travel_badge"><i className="fa-solid fa-car"></i></div>
                                                            : apptNo}
                                                    </td>
                                                    <td style={cellStyle}>
                                                        {record.is_travel
                                                            ? (record.appid || '—')
                                                            : <a
                                                                className="link_preview"
                                                                href={`/appointments/view/${record.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {record.appid || '—'}
                                                            </a>}
                                                    </td>
                                                    <td style={cellStyle}>{typeLabel(record)}</td>
                                                    <td style={cellStyle}>{formatDateTimeRange(record)}</td>
                                                    <td style={cellStyle}>{record.is_travel ? '—' : (record.school || '—')}</td>
                                                    <td style={cellStyle}>
                                                        {record.is_travel ? '—' : formatAssignDateTime(
                                                            record.appointment_assign?.checkin_date,
                                                            record.appointment_assign?.checkin_time
                                                        )}
                                                    </td>
                                                    <td style={cellStyle}>
                                                        {record.is_travel ? '—' : formatAssignDateTime(
                                                            record.appointment_assign?.checkout_date,
                                                            record.appointment_assign?.checkout_time
                                                        )}
                                                    </td>
                                                    <td style={cellStyle}>{formatMinutes(record.duration_minutes)}</td>
                                                    <td style={cellStyle}>
                                                        {record.is_travel
                                                            ? <span className="payroll_travel_chip">Travel Time</span>
                                                            : record.status === 'completed'
                                                                ? <span className="payroll_completed_chip">Completed</span>
                                                                : <span className="text-capitalize">{record.status || '—'}</span>}
                                                    </td>
                                                    {canCheckin &&
                                                        <td style={cellStyle}>
                                                            {!record.is_travel && (
                                                                <button
                                                                    type="button"
                                                                    className="payroll_action_btn"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#checkinCheckoutAdjustmentModal"
                                                                    onClick={() => onCheckinCheckout(record)}
                                                                >
                                                                    {record.status === 'completed' ? 'Adjust Check-in/out' : 'Check-in/out'}
                                                                </button>
                                                            )}
                                                        </td>
                                                    }
                                                </tr>
                                            );
                                        }); })()}
                                    </tbody>
                                </table>
                            </div>
                            <div className="payroll_totals_strip">
                                <div className="payroll_totals_item">
                                    <span className="payroll_icon_circle"><i className="fa-solid fa-clock"></i></span>
                                    <div>
                                        <span className="label">Meeting Hours</span>
                                        <span className="value">{formatMinutes(interpreter.meeting_minutes)}</span>
                                    </div>
                                </div>
                                <div className="payroll_totals_item">
                                    <span className="payroll_icon_circle"><i className="fa-solid fa-car"></i></span>
                                    <div>
                                        <span className="label">Travel Time</span>
                                        <span className="value">{formatMinutes(interpreter.travel_minutes)}</span>
                                    </div>
                                </div>
                                <div className="payroll_totals_item">
                                    <span className="payroll_icon_circle"><i className="fa-solid fa-calculator"></i></span>
                                    <div>
                                        <span className="label">Total Time</span>
                                        <span className="value">{formatMinutes(interpreter.total_minutes)}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center p-3 payroll_muted">
                            No appointments in selected range
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function Payroll() {
    const dispatch = useDispatch();
    const { payroll, loader } = useSelector(payrollSelector);
    const { loader: actionLoader } = useSelector(actionButtonsSelector);
    const { checkPermission } = usePermissions();
    const [openId, setOpenId] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [exportingId, setExportingId] = useState(null); // interpreter id or 'all'
    const [currentRange, setCurrentRange] = useState({
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    });

    const canCheckin = checkPermission('Check-in/Check-out');

    const { control, watch } = useForm({
        defaultValues: {
            start_date: moment().startOf('month').format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
        }
    });

    const startDate = watch('start_date');
    const endDate = watch('end_date');

    const handleSearch = () => {
        const params = {
            start_date: moment(startDate).format('YYYY-MM-DD'),
            end_date: moment(endDate).format('YYYY-MM-DD'),
        };
        setOpenId(null);
        setCurrentRange(params);
        dispatch(fetchPayroll(params));
    };

    useEffect(() => {
        dispatch(fetchPayroll(currentRange));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Save handler for the shared check-in/checkout modal — same endpoint the
    // appointment view uses; marks the appointment completed, then refresh.
    const handleAdjustmentSubmit = (data, endpoint, closeModal) => {
        if (!selectedRecord?.id) return;
        dispatch(setActionAssign(selectedRecord.id, data, endpoint, (success, message) => {
            if (success) {
                toast.success(message || 'Appointment adjusted successfully.', { className: 'successToast' });
                closeModal();
                setSelectedRecord(null);
                dispatch(fetchPayroll(currentRange, null));
            } else {
                toast.error(message || 'Failed to save check-in/checkout.', { className: 'errorToast' });
            }
        }));
    };

    const handleExport = (interpreter) => {
        const id = interpreter ? interpreter.id : 'all';
        setExportingId(id);
        const params = { ...currentRange };
        if (interpreter) params.interpreter_id = interpreter.id;

        dispatch(downloadPayroll(params, (success, result) => {
            setExportingId(null);
            if (!success) {
                toast.error(result || 'Failed to export payroll.', { className: 'errorToast' });
                return;
            }
            const namePart = interpreter ? ` - ${interpreter.name}` : '';
            downloadBlobFile(result, `Payroll - ${params.start_date} - ${params.end_date}${namePart}.xlsx`);
            toast.success('Payroll exported successfully.', { className: 'successToast' });
        }));
    };

    const summary = payroll?.summary;

    return (
        <>
            <div className="content">
                <div className="row_main_head">
                    <h2 className="main_heading">Payroll</h2>
                </div>

                {/* Date Range Filter */}
                <div className="card_table p-4 mb-4">
                    <div className="row-primary dahboard_filter reports">
                        <DateRangeFilter control={control} />
                        <button
                            type="button"
                            className="main_button green"
                            onClick={handleSearch}
                        >
                            Search
                        </button>
                    </div>
                </div>

                {/* Summary cards */}
                <div className="row_dash_1">
                    <SummaryCard icon="fa-users" label="Total Interpreters" value={summary?.total_interpreters ?? 0} />
                    <SummaryCard icon="fa-calendar-days" label="Appointments" value={summary?.appointments ?? 0} />
                    <SummaryCard icon="fa-clock" label="Meeting Hours" value={formatMinutes(summary?.meeting_minutes)} />
                    <SummaryCard icon="fa-car" label="Travel Time" value={formatMinutes(summary?.travel_minutes)} />
                    <SummaryCard icon="fa-calculator" label="Total Hours" value={formatMinutes(summary?.total_minutes)} />
                </div>

                {/* Interpreter payroll details */}
                <div className="card_table p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: 12 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Interpreter Payroll Details</h2>
                        <button
                            type="button"
                            className="main_button"
                            disabled={exportingId === 'all' || !summary?.appointments}
                            onClick={() => handleExport(null)}
                        >
                            {exportingId === 'all' ? 'Exporting…' : 'Export All'}
                        </button>
                    </div>
                    {payroll?.interpreters?.map((interpreter) => (
                        <InterpreterRow
                            key={interpreter.id}
                            interpreter={interpreter}
                            isOpen={openId === interpreter.id}
                            onToggle={() => setOpenId(openId === interpreter.id ? null : interpreter.id)}
                            onCheckinCheckout={setSelectedRecord}
                            onExport={handleExport}
                            exporting={exportingId === interpreter.id}
                            canCheckin={canCheckin}
                        />
                    ))}
                    {!loader && !payroll?.interpreters?.length && (
                        <div className="text-center p-4 payroll_muted">No interpreters found.</div>
                    )}
                </div>
            </div>

            <CheckinCheckoutAdjustmentModal
                item={selectedRecord}
                onSubmit={handleAdjustmentSubmit}
                onClose={() => setSelectedRecord(null)}
                loader={actionLoader}
            />

            {loader && <FullPageLoader />}
        </>
    );
}

export default Payroll;
